import { ProductCatalogSearchQueryFiltersSelectionGroupMonetaryRangeInput } from '@graphql/generated';
import { RangeValue } from '../components/RangeInput';
import { CatalogRegistryTranslations } from '@locales/generated';

/**
 * Get absolute price range
 * @param value RangeValue
 * @returns RangeValue
 */
export const getAbsPriceRange = (value: RangeValue): RangeValue => ({
  min: value.min ? getAbsoluteValue(value.min) : undefined,
  max: value.max ? getAbsoluteValue(value.max) : undefined,
});

const getAbsoluteValue = (value: number) => (value < 0 ? Math.abs(value) : value);

/**
 * Get consistent price range
 * @param value RangeValue
 * @param updatedValue 'min' | 'max' | undefined
 * @returns RangeValue
 */
export const getConsistentPriceRange = (value: RangeValue, updatedValue?: 'min' | 'max'): RangeValue => {
  if (value.min && value.max) {
    /**
     * If min has changed and min is greater than max, then set max to min + 5
     */
    if (updatedValue === 'min' && value.min > value.max) {
      return { min: value.min, max: value.min + 5 };
    }

    /**
     * If max has changed and max is less than min, then set min to max - 5
     */
    if (updatedValue === 'max' && value.max < value.min) {
      return { min: value.max <= 5 ? 0 : value.max - 5, max: value.max };
    }
  }

  return value;
};

/**
 * Get absolute consistent price range
 * @param value RangeValue
 * @param updatedValue 'min' | 'max' | undefined
 * @returns RangeValue
 */
export const getVerifiedPriceRange = (value: RangeValue, updatedValue?: 'min' | 'max'): RangeValue => {
  if (value.max === 0) return { ...value, max: undefined };
  const absValue = getAbsPriceRange(value);
  return getConsistentPriceRange(absValue, updatedValue);
};

/**
 * Compose a string with the min-max values in minor units
 * E.g: 10000-20000 | 10000- | -20000
 * @param value RangeValue
 * @returns string
 */
export const getComposedStringFromRange = (value: RangeValue): string => {
  const minValueInMinorUnits = value.min ? value.min * 100 : '';
  const maxValueInMinorUnits = value.max ? value.max * 100 : '';
  return `${minValueInMinorUnits}-${maxValueInMinorUnits}`;
};

/**
 * Get min-max values from a string in minor units
 * E.g: 10000-20000 | 10000- | -20000
 * @param value string
 * @returns RangeValue
 */
export const getRangeFromComposedString = (value: string): RangeValue => {
  const [min, max] = value.split('-');
  const minInt = parseInt(min);
  const maxInt = parseInt(max);
  return {
    min: !isNaN(minInt) ? minInt : undefined,
    max: !isNaN(maxInt) ? maxInt : undefined,
  };
};

/**
 * Get a valid selectedPriceRange value from a string in minor units
 * @param value string
 * @returns ProductCatalogSearchQueryFiltersSelectionGroupMonetaryRangeInput
 */
export const getSelectedPriceRangeFromString = (value: string): ProductCatalogSearchQueryFiltersSelectionGroupMonetaryRangeInput => {
  const { min, max } = getRangeFromComposedString(value);
  return {
    minValueInMinorUnits: min ?? null,
    maxValueInMinorUnits: max ?? null,
  };
};

type BucketRange = { gt?: string; lt?: string };
type RefinementsPanel = CatalogRegistryTranslations['refinementsPanel'];

export const getRangeFromBucketCode = (code: string): BucketRange => {
  const regex = /(\d+)/g;
  const numberValues = code.match(regex);
  const range: BucketRange = {};
  if (!numberValues?.length) return range;

  if (code.includes('gt')) {
    range['gt'] = numberValues[0];
  }
  if (code.includes('lt')) {
    range['lt'] = numberValues[numberValues.length - 1];
  }
  return range;
};

export const getTitleFromBucketCode = (code: string, refinementsPanel: RefinementsPanel, hasPrefix: boolean = false): string => {
  const { gt, lt } = getRangeFromBucketCode(code);
  const prefix = hasPrefix ? refinementsPanel.pricePrefix() : '';
  if (gt && lt) return `${prefix}${refinementsPanel.priceBucketRange({ minValue: gt, maxValue: lt })}`;
  if (gt) return `${prefix}${refinementsPanel.priceAndMore({ minValue: gt })}`;
  if (lt) return `${prefix}${refinementsPanel.priceAndLess({ maxValue: lt })}`;
  return refinementsPanel.custom();
};
